import { capitalize } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { CalendarFilter, CalendarFilterType } from '../../models/CalendarFilter'
import { Item } from '../../models/Item'
import { User } from '../../models/User'
import { getSingularType } from '../../utils/calendar'
import { currencyFormat, formatDuration } from '../../utils/numbers'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { SelectList, SelectListItem } from '../common/SelectList'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { CalendarEmptyState } from './CalendarEmptyState'
import { CalendarSelectCard } from './CalendarSelectCard'

type Props = {
    filter: CalendarFilter
    pros: User[]
    items: Item[]
    isOpen: boolean
    onDone: (filter: CalendarFilter) => void
    onClose: () => void
}

export const CalendarFilterModal = ({ filter, pros, items, isOpen, onDone, onClose }: Props) => {
    const [type, setType] = useState<CalendarFilterType | null>(null)
    const [selectedItemsByType, setSelectedItemsByType] = useState<
        Record<CalendarFilterType, string[]>
    >(() => ({
        pros: filter.pros,
        services: filter.services,
        classes: filter.classes,
        reservations: filter.reservations,
        resources: filter.resources,
    }))

    useEffect(() => {
        setSelectedItemsByType({
            pros: filter.pros,
            services: filter.services,
            classes: filter.classes,
            reservations: filter.reservations,
            resources: filter.resources,
        })
    }, [filter])

    const getIcon = (t: CalendarFilterType) => {
        switch (t) {
            case 'pros':
                return <Icon icon="users.large" background="round" />
            case 'services':
                return <Icon icon="tag.large" background="round" />
            case 'classes':
                return <Icon icon="student.large" background="round" />
            case 'reservations':
                return <Icon icon="calendarClock.large" background="round" />
        }
    }

    const selectItems: SelectListItem[] = useMemo(() => {
        if (!type) return []

        if (type === 'pros') {
            return pros
                .filter((p) => !!p.id && !!p.title)
                .map((p) => ({
                    id: p.id ?? '',
                    title: p.title ?? '',
                    tertiaryView: <ContactAvatar user={p} size="md" />,
                }))
        }

        return items
            .filter((i) => !!i.id && !!i.title && !!type && i.type === getSingularType(type))
            .map((i) => ({
                id: i.id ?? '',
                title: i.title ?? '',
                subtitle: `${currencyFormat(i.rate)} ${i.duration ? `· ${formatDuration(i.duration)}` : ''}`,
                tertiaryView: i.image?.url ? (
                    <img className="size-12 rounded-full" alt={i.name} src={i.image.url} />
                ) : (
                    getIcon(type)
                ),
            }))
    }, [type, pros, items])

    const selectedItems = useMemo(() => {
        if (!type) return []
        return selectItems.filter((item) => selectedItemsByType[type].includes(item.id))
    }, [type, selectItems, selectedItemsByType])

    const clearAll = useCallback(() => {
        if (type) {
            setSelectedItemsByType((prev) => ({
                ...prev,
                [type]: [],
            }))
        }
        else {
            setSelectedItemsByType({
                pros: [],
                services: [],
                classes: [],
                reservations: [],
                resources: [],
            })
        }
    }, [type])

    const updateSelectedItems = useCallback(
        (items: SelectListItem[]) => {
            if (!type) return
            setSelectedItemsByType((prev) => ({
                ...prev,
                [type]: items.map((item) => item.id),
            }))
        },
        [type]
    )

    const handleDone = useCallback(() => {
        const newFilter = new CalendarFilter()
        Object.entries(selectedItemsByType).forEach(([type, ids]) => {
            ids.forEach((id) => newFilter.addIdByType(id, type as CalendarFilterType))
        })
        onDone(newFilter)
    }, [selectedItemsByType, onDone])

    const canClearAll = useMemo(() => {
        if (!type) {
            return Object.values(selectedItemsByType).some((ids) => ids.length > 0)
        }
        return selectedItemsByType[type].length > 0
    }, [type, selectedItemsByType])

    const handleClose = () => {
        setType(null)
        setSelectedItemsByType({
            pros: filter.pros,
            services: filter.services,
            classes: filter.classes,
            reservations: filter.reservations,
            resources: filter.resources,
        })
        onClose()
    }

    return (
        <Modal
            isOpen={isOpen}
            title={
                type ? (
                    <span>
                        {capitalize(type)}{' '}
                        {!!selectedItems.length && (
                            <Label className="text-gray-600">({selectedItems.length})</Label>
                        )}
                    </span>
                ) : (
                    'Filter'
                )
            }
            onNavigateBack={!!type ? () => setType(null) : undefined}
            onClose={handleClose}
            footer={
                <div className="flex w-full gap-4">
                    <Button
                        className="text-primary-700 mr-auto"
                        disabled={!canClearAll}
                        onClick={clearAll}
                    >
                        Clear all
                    </Button>

                    <Button intent="primary" onClick={!!type ? () => setType(null) : handleDone}>
                        Done
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col gap-3">
                {!type &&
                    Object.values(CalendarFilterType)
                        .filter((t) => t !== 'resources')
                        .map((t) => (
                            <CalendarSelectCard
                                key={t}
                                className="p-4"
                                left={
                                    <div className="flex items-center gap-4">
                                        {getIcon(t)}
                                        <Label className="text-gray-700 capitalize">{t}</Label>
                                    </div>
                                }
                                right={
                                    <div className="flex items-center gap-4">
                                        {selectedItemsByType[t].length > 0 && (
                                            <Paragraph className="text-secondary-400">
                                                {selectedItemsByType[t].length} selected
                                            </Paragraph>
                                        )}
                                        <Icon icon="chevronRight.large" className="text-gray-500" />
                                    </div>
                                }
                                onSelect={() => setType(t)}
                            />
                        ))}

                {!!type && selectItems.length > 0 && (
                    <SelectList
                        selectionType="multi"
                        items={selectItems}
                        selectedItems={selectedItems}
                        onSelectItems={updateSelectedItems}
                        canSearch={selectItems.length > 6}
                    />
                )}
                {!!type && selectItems.length === 0 && (
                    <CalendarEmptyState
                        src={'/icons/checklist.svg'}
                        title={`No ${capitalize(type)}`}
                        subtitle={`${`You haven't added any ${type} yet`}`}
                    />
                )}
            </div>
        </Modal>
    )
}
